const STORAGE_PREFIX = 'coloringApp_'; // Prefix for localStorage keys
const DB_NAME = 'ColoringAppDB';
const DB_VERSION = 1;
const STORE_NAME = 'items';

export class PersistentManager {
    private static instance: PersistentManager;
    private db: IDBDatabase | null = null;

    private readonly prefix: string;
    private constructor() {
        this.prefix = STORAGE_PREFIX;
        this.openDatabase();
    }

    public static getInstance(): PersistentManager {
        if (!PersistentManager.instance) {
            PersistentManager.instance = new PersistentManager();
        }
        return PersistentManager.instance;
    }

    private _getKey(key: string): string {
        return `${this.prefix}${key}`;
    }

    private openDatabase(): void {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        };

        request.onsuccess = (event) => {
            this.db = (event.target as IDBOpenDBRequest).result;
        };

        request.onerror = (event) => {
            console.error('Error opening IndexedDB', event);
        };
    }

    setItem(key: string, value: any): void {
        if (this.isIndexedDBAvailable() && this.db) {
            const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const item = { key: this._getKey(key), value: value };

            const request = store.put(item);
            request.onerror = (event) => {
                console.error('Error saving to IndexedDB', event);
            };
        } else {
            try {
                const serializedValue = JSON.stringify(value);
                localStorage.setItem(this._getKey(key), serializedValue);
            } catch (error) {
                console.error('Error saving to localStorage', error);
            }
        }
    }

    getItem(key: string): Promise<any> {
        if (this.isIndexedDBAvailable() && this.db) {
            return new Promise((resolve) => {
                const transaction = this.db!.transaction(STORE_NAME, 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get(this._getKey(key));

                request.onsuccess = (event) => {
                    const result = (event.target as IDBRequest).result;
                    resolve(result ? result.value : null);
                };

                request.onerror = (event) => {
                    console.error('Error retrieving from IndexedDB', event);
                    resolve(null);
                };
            });
        } else {
            try {
                const serializedValue = localStorage.getItem(this._getKey(key));
                return Promise.resolve(serializedValue ? JSON.parse(serializedValue) : null);
            } catch (error) {
                console.error('Error retrieving from localStorage', error);
                return Promise.resolve(null);
            }
        }
    }

    removeItem(key: string): void {
        if (this.isIndexedDBAvailable() && this.db) {
            const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete(this._getKey(key));

            request.onerror = (event) => {
                console.error('Error removing from IndexedDB', event);
            };
        } else {
            try {
                localStorage.removeItem(this._getKey(key));
            } catch (error) {
                console.error('Error removing from localStorage', error);
            }
        }
    }

    clear(): void {
        if (this.isIndexedDBAvailable() && this.db) {
            const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.clear();

            request.onerror = (event) => {
                console.error('Error clearing IndexedDB', event);
            };
        } else {
            try {
                const keysToRemove = Object.keys(localStorage).filter(key => key.startsWith(this.prefix));
                keysToRemove.forEach(key => localStorage.removeItem(key));
            } catch (error) {
                console.error('Error clearing localStorage', error);
            }
        }
    }

    async exists(key: string): Promise<boolean> {
        const value = await this.getItem(key);
        return value !== null;
    }

    private isIndexedDBAvailable(): boolean {
        return !!window.indexedDB;
    }
}
